(function () {
  // ---------------------------------------------------------------------------
  // Consent gated Doctify review widget.
  //
  // Mirrors assets/src/js/lib/video.js and assets/src/js/lib/spotify.js: the
  // page HTML is cached before consent is known, so the server never ships an
  // active Doctify <script src="...">. Instead each `.doctify-sec__widget` host
  // carries its own remote URL in `data-script-src`, and the linked-image
  // fallback (`.doctify-sec__fallback`) is what renders until the visitor
  // grants "statistics" consent.
  //
  // Several `.doctify-sec` sections can render on one page, so every piece of
  // state is scoped to its section:
  //
  //   * the original host keeps Doctify's generated `id`; client-side clones
  //     that copy it get a fresh runtime id, and the matching
  //     `widget_container_id` in their URL is updated before injection;
  //   * the injected script is a child of that section (placed next to its
  //     host), found again via `section.querySelector`;
  //   * `.is-consented` is toggled on that one section only.
  //
  // Lifecycle (per section):
  //
  //   pre-consent   `.doctify-sec`               linked fallback images, no
  //                                              third party contacted
  //   consented     `.doctify-sec.is-consented`  that section's script is created
  //                                              and appended exactly once; the
  //                                              widget renders into its own host
  //   revoked       `.doctify-sec`               that section's script removed,
  //                                              widget content / iframes cleared,
  //                                              linked fallback restored
  //
  // Everything fails closed: an absent `wp_has_consent`, a denied/revoked
  // "statistics" choice, a missing host/URL, or a script load failure all leave
  // the third party unloaded and the fallback on screen.
  // ---------------------------------------------------------------------------
  var CONSENT_CATEGORY = "statistics";
  var SCRIPT_MARKER = "data-doctify-widget-script";
  var WIDGET_SELECTOR = ".doctify-sec__widget";

  // Sections that already have (or failed to get) their script injected. Guards
  // against duplicate loading across repeated allow events even if the remote
  // loader removes its own script tag from the DOM after it runs.
  var injectedSections = new WeakSet();

  // Runs the added-section detection. Held so it is only ever connected once.
  var observer = null;

  // Counter behind the runtime ids minted for cloned/duplicate hosts.
  var runtimeIdCounter = 0;

  function sections() {
    return document.querySelectorAll(".doctify-sec");
  }

  function hasConsent() {
    if (typeof window.wp_has_consent !== "function") {
      return false;
    }

    try {
      return !!window.wp_has_consent(CONSENT_CATEGORY);
    } catch (e) {
      return false;
    }
  }

  // This section's widget host. Everything else is derived from it, so a
  // section without a host simply stays inert (fallback remains).
  function hostFor(section) {
    return section.querySelector(WIDGET_SELECTOR);
  }

  function scriptSrcFor(section) {
    var host = hostFor(section);
    return host ? host.getAttribute("data-script-src") || "" : "";
  }

  // The script this module injected for this section, if any. Doubles as the
  // "already injected" guard so repeated allow events never duplicate.
  function injectedScriptFor(section) {
    return section.querySelector("script[" + SCRIPT_MARKER + "]");
  }

  function setConsented(section, on) {
    section.classList.toggle("is-consented", !!on);
  }

  // Clears everything the Doctify script rendered into this section's host —
  // iframes are removed explicitly (some media states keep them alive
  // otherwise) before the remaining generated nodes are dropped, so a revoked
  // consent leaves no third-party content and the fallback takes the space
  // back.
  function clearWidget(section) {
    var host = hostFor(section);

    if (!host) {
      return;
    }

    var iframes = host.querySelectorAll("iframe");

    for (var i = 0; i < iframes.length; i++) {
      if (iframes[i].parentNode) {
        iframes[i].parentNode.removeChild(iframes[i]);
      }
    }

    while (host.firstChild) {
      host.removeChild(host.firstChild);
    }
  }

  function removeScript(section) {
    var script = injectedScriptFor(section);

    if (script && script.parentNode) {
      script.parentNode.removeChild(script);
    }
  }

  // A runtime id that collides with nothing currently in the document. The PHP
  // ids look like `doctify-1`; these are namespaced so they never clash.
  function runtimeId() {
    var id;

    do {
      runtimeIdCounter += 1;
      id = "doctify-js-" + runtimeIdCounter;
    } while (document.getElementById(id));

    return id;
  }

  // Rewrites only the `widget_container_id` query value, preserving every other
  // Doctify parameter (and the original ordering).
  function withContainerId(src, id) {
    if (!src) {
      return src;
    }

    if (/([?&])widget_container_id=/.test(src)) {
      return src.replace(/([?&]widget_container_id=)[^&]*/, "$1" + id);
    }

    return (
      src + (src.indexOf("?") === -1 ? "?" : "&") + "widget_container_id=" + id
    );
  }

  // Every section must own a unique container id: the PHP mints one per render,
  // but client-side code can clone a section — copying its id and URL — which
  // would leave two widgets fighting over one Doctify target. Walk the hosts in
  // document order, keep the first id seen, and mint a fresh runtime id/URL for
  // any later duplicate (or id-less) host.
  function reconcile() {
    var hosts = document.querySelectorAll(WIDGET_SELECTOR);
    var seen = Object.create(null);

    for (var i = 0; i < hosts.length; i++) {
      var host = hosts[i];
      var section = host.closest(".doctify-sec");

      if (!section) {
        continue;
      }

      var id = host.id;

      if (id && !seen[id]) {
        seen[id] = true;
        continue;
      }

      var newId = runtimeId();
      host.id = newId;
      seen[newId] = true;

      var src = host.getAttribute("data-script-src");

      if (src) {
        host.setAttribute("data-script-src", withContainerId(src, newId));
      }

      // The old id's rendering (including any iframe a deep clone copied in)
      // belongs to the old target: clear it so the fresh script can build the
      // widget again under the new id.
      clearWidget(section);

      // This section was already wired against the old id: tear the stale
      // script down so apply() re-injects a fresh one for the new URL.
      if (injectedSections.has(section)) {
        injectedSections.delete(section);
        removeScript(section);
      }
    }
  }

  // A cloned section can carry a copy of an injected <script> node. The
  // per-section WeakSet is the authority on what this module created, so any
  // script whose section is not in it is a copy: drop it, and apply() then
  // injects a fresh script keyed to that section's reconciled id.
  function removeCopiedScripts() {
    var scripts = document.querySelectorAll("script[" + SCRIPT_MARKER + "]");

    for (var i = 0; i < scripts.length; i++) {
      var script = scripts[i];
      var section = script.closest(".doctify-sec");

      if (section && injectedSections.has(section)) {
        continue;
      }

      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    }
  }

  // Only called once this section's "statistics" consent is known to be granted.
  // The DOM query for the marker makes repeated allow events a no-op.
  function inject(section) {
    if (injectedSections.has(section)) {
      return;
    }

    if (injectedScriptFor(section)) {
      return;
    }

    var src = scriptSrcFor(section);

    if (!src) {
      // No remote URL: keep the linked fallback rather than blanking the slot.
      return;
    }

    var host = hostFor(section);
    var script = document.createElement("script");
    script.setAttribute(SCRIPT_MARKER, "true");
    script.async = true;
    script.src = src;

    // A remote script that never renders must not leave the section in the
    // consented state: tear the node down, drop the injected guard and restore
    // the fallback, so the section behaves as if the widget never loaded (a
    // later allow event may try again).
    script.addEventListener("error", function () {
      injectedSections.delete(section);

      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }

      clearWidget(section);
      setConsented(section, false);
    });

    injectedSections.add(section);

    // Insert next to this section's host so the script resolves the right
    // container id and the node stays scoped to the section (making the revoke
    // removal above a single, section-local operation).
    if (host && host.parentNode) {
      host.parentNode.insertBefore(script, host.nextSibling);
    } else {
      (document.body || document.head || document.documentElement).appendChild(
        script,
      );
    }
  }

  function grant(section) {
    if (!scriptSrcFor(section)) {
      return;
    }

    inject(section);
    setConsented(section, true);
  }

  function revoke(section) {
    injectedSections.delete(section);
    removeScript(section);
    clearWidget(section);
    setConsented(section, false);
  }

  // Single source of truth for the two states, applied per section. Reads
  // consent fresh every time so a late-initialising wp_has_consent is honoured
  // on the next evaluation.
  function apply(section) {
    if (hasConsent()) {
      grant(section);
    } else {
      revoke(section);
    }
  }

  function evaluate() {
    // Reconcile container ids / URLs first, so a cloned or duplicated section
    // never shares a target with the original, then drop any script a clone
    // dragged along before consent is applied.
    reconcile();
    removeCopiedScripts();

    var els = sections();

    for (var i = 0; i < els.length; i++) {
      apply(els[i]);
    }
  }

  // Nodes this module — or the remote widget loader filling its host — owns.
  // The observer skips them so its own injections never re-enter evaluate().
  function isSelfOwnedNode(node) {
    if (node.matches("script[" + SCRIPT_MARKER + "]")) {
      return true;
    }

    if (node.matches && node.matches(WIDGET_SELECTOR)) {
      return true;
    }

    return !!(node.closest && node.closest(WIDGET_SELECTOR));
  }

  // True only when a `.doctify-sec` was added somewhere in this batch of
  // mutations (an added wrapper that contains one also counts).
  function addedDoctifySection(mutations) {
    for (var m = 0; m < mutations.length; m++) {
      var nodes = mutations[m].addedNodes;

      for (var i = 0; i < nodes.length; i++) {
        var node = nodes[i];

        if (node.nodeType !== 1) {
          continue;
        }

        if (isSelfOwnedNode(node)) {
          continue;
        }

        if (node.matches(".doctify-sec")) {
          return true;
        }

        if (node.querySelector && node.querySelector(".doctify-sec")) {
          return true;
        }
      }
    }

    return false;
  }

  function observeSections() {
    if (
      observer ||
      typeof window.MutationObserver !== "function" ||
      !document.body
    ) {
      return;
    }

    observer = new window.MutationObserver(function (mutations) {
      if (addedDoctifySection(mutations)) {
        evaluate();
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  document.addEventListener("wp_listen_for_consent_change", function (e) {
    if (!e.detail || !e.detail[CONSENT_CATEGORY]) {
      return;
    }

    if (e.detail[CONSENT_CATEGORY] === "allow") {
      evaluate();
    } else if (e.detail[CONSENT_CATEGORY] === "deny") {
      // Re-read consent rather than trusting the event: an explicit deny that
      // is not yet reflected still fails closed through hasConsent().
      evaluate();
    }
  });

  document.addEventListener("wp_consent_type_defined", function () {
    evaluate();
  });

  function ready(fn) {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", fn);
    } else {
      fn();
    }
  }

  ready(function () {
    evaluate();

    // Watch for client-side clones of the section appearing after init.
    observeSections();

    // Safety net for a consent script that loads after the app bundle.
    if (typeof window.wp_has_consent !== "function") {
      setTimeout(evaluate, 2000);
    }
  });
})();
